/* DentPDR commerce elements (design.md sections 7, 8, 17): <product-form>, <variant-picker>, <qv-gallery>,
   <quick-view>, <cart-drawer>, <cart-items> and the delegated quick view, quick add and cart open listeners.
   Needs window.DP from dp-global.js (trapFocus, lockScroll, toast, root) at call time, not at load. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  const root = () => (window.DP && DP.root) || (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  let S = {};
  try { S = JSON.parse($('#dp-commerce-strings').textContent); } catch (e) { /* strings are optional */ }
  const text = (key, fallback) => S[key] || fallback || '';

  // POST JSON. Rejects with .status set only when the server answered; network failures stay generic.
  const post = (path, body, signal) => fetch(root() + path, {
    method: 'POST', signal,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  }).then(async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(res.status === 422 ? data.description || data.message || '' : ''), { status: res.status });
    return data;
  });
  const errorText = (err, fallback) => (err.status === 422 && err.message) || fallback || text('generic');

  let liveRegion;
  const announce = (msg) => {
    if (!liveRegion) {
      liveRegion = Object.assign(document.createElement('div'), { className: 'dp-visually-hidden' });
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.append(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(() => { liveRegion.textContent = msg; }, 50);
  };

  const cartSections = () => ['cart-drawer', ($('cart-items[data-context="page"]') || {}).dataset?.sectionId].filter(Boolean);

  // Re-render the drawer and the cart page from a Section Rendering API response.
  function applyCart(sections) {
    const drawer = $('cart-drawer');
    const page = $('cart-items[data-context="page"]');
    let rendered = false;
    if (drawer && sections && sections['cart-drawer']) { drawer.render(sections['cart-drawer']); rendered = true; }
    if (page && sections && sections[page.dataset.sectionId]) page.render(sections[page.dataset.sectionId]);
    return rendered;
  }

  // Add lines, refresh the cart sections, then open the drawer (D10) or fall back to a toast.
  async function addItems(items, source, returnTo, beforeOpen) {
    const data = await post('cart/add.js', { items, sections: cartSections().join(','), sections_url: location.pathname });
    if (beforeOpen) beforeOpen();
    const rendered = applyCart(data.sections);
    const drawer = $('cart-drawer');
    let count = rendered ? +drawer.dataset.itemCount : 0;
    if (!rendered) count = (await fetch(root() + 'cart.js').then((r) => r.json()).catch(() => ({}))).item_count || 0;
    emit('dp:cart:updated', { itemCount: count, source, drawer: rendered });
    if (rendered) emit('dp:cart:open', { addedKey: (data.items && data.items[0] && data.items[0].key) || null, returnTo });
    return data;
  }

  /* ---------- overlay base (drawer and modal pattern of the design system) ---------- */
  class Overlay extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); this.close(); } };
      this.addEventListener('click', (e) => { if (e.target.closest('[data-dp-close]')) this.close(); });
    }
    get isOpen() { return this.hasAttribute('open'); }
    get panel() { return this.querySelector('[role="dialog"]'); }
    open({ returnTo } = {}) {
      if (this.isOpen) return;
      this._return = returnTo || document.activeElement;
      this.setAttribute('open', '');
      this.panel.setAttribute('data-open', '');
      DP.lockScroll();
      this._release = DP.trapFocus(this.panel, this._return);
      document.addEventListener('keydown', this._esc);
      const first = $('button[data-dp-close]', this.panel) || this.panel;
      first.focus();
      emit('dp:overlay:change', { open: true });
    }
    close({ restore = true } = {}) {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.panel.removeAttribute('data-open');
      document.removeEventListener('keydown', this._esc);
      if (this._release) this._release();
      DP.unlockScroll();
      if (restore && this._return && this._return.isConnected) this._return.focus();
      emit('dp:overlay:change', { open: false });
      this.afterClose && this.afterClose();
    }
  }
  // dp-product.js builds the zoom viewer and the material drawer on the same base.
  window.DPOverlay = Overlay;

  /* ---------- <cart-drawer> ---------- */
  class CartDrawer extends Overlay {
    connectedCallback() {
      super.connectedCallback();
      if (this._wired) return;
      this._wired = true;
      document.addEventListener('dp:cart:open', (e) => this.show(e.detail || {}));
      this.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-dp-upsell-toggle]');
        if (toggle) {
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', open);
          $('#' + toggle.getAttribute('aria-controls'), this).hidden = !open;
        }
        const add = e.target.closest('[data-dp-upsell-add]');
        if (add) this.upsell(add);
      });
    }
    show({ addedKey, returnTo } = {}) {
      this.open({ returnTo });
      if (!addedKey) return;
      const line = $(`[data-key="${CSS.escape(addedKey)}"]`, this);
      const note = $('[data-dp-added]', this);
      if (!line || !note) return;
      line.setAttribute('data-new', '');
      $('[data-dp-added-text]', note).textContent = line.dataset.added;
      note.hidden = false;
    }
    afterClose() { this.clearAdded(); }
    clearAdded() {
      const note = $('[data-dp-added]', this);
      if (note) note.hidden = true;
      $$('[data-new]', this).forEach((li) => li.removeAttribute('data-new'));
    }
    render(html) {
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
      const inner = $('[data-dp-cart-inner]', this);
      if (!next || !inner) return;
      const scroll = $('[data-dp-scroll]', this);
      const top = scroll ? scroll.scrollTop : 0;
      this.dataset.itemCount = next.dataset.itemCount;
      inner.replaceWith(document.importNode($('[data-dp-cart-inner]', next), true));
      const fresh = $('[data-dp-scroll]', this);
      if (fresh) fresh.scrollTop = top;
    }
    async upsell(btn) {
      const scroll = $('[data-dp-scroll]', this);
      const overlay = $('[data-dp-overlay]', this);
      if (overlay) overlay.hidden = false;
      if (scroll) scroll.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      try { await addItems([{ id: +btn.dataset.dpUpsellAdd, quantity: 1 }], 'upsell'); }
      catch (err) {
        btn.disabled = false;
        if (overlay) overlay.hidden = true;
        if (scroll) scroll.removeAttribute('aria-busy');
        DP.toast({ type: 'error', title: text('addTitle'), body: errorText(err) });
      }
    }
  }

  /* ---------- <cart-items>: line changes in the drawer and on the cart page ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.timers = new Map();
      this.ctrls = new Map();
      this.addEventListener('input', (e) => this.queue(e));
      this.addEventListener('change', (e) => this.queue(e));
      // Enter in a quantity field would submit the form to checkout; send the change instead.
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !e.target.matches('[data-dp-qty]')) return;
        e.preventDefault();
        const line = e.target.closest('[data-dp-line]');
        const qty = parseInt(e.target.value, 10);
        if (!line || !(qty >= 1)) return;
        clearTimeout(this.timers.get(line.dataset.key));
        this.send(line, qty);
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-dp-remove]');
        if (!rm) return;
        e.preventDefault();
        this.send(rm.closest('[data-dp-line]'), 0);
      });
      if (this.dataset.context === 'page') this.watchBar();
    }
    queue(e) {
      const input = e.target;
      if (!input.matches || !input.matches('[data-dp-qty]')) return;
      const line = input.closest('[data-dp-line]');
      const qty = parseInt(input.value, 10);
      if (!line || !(qty >= 1)) return;
      clearTimeout(this.timers.get(line.dataset.key));
      this.timers.set(line.dataset.key, setTimeout(() => this.send(line, qty), 300));
    }
    busy(line, on) {
      line.toggleAttribute('data-updating', on);
      const spin = $('[data-dp-updating]', line);
      if (spin) spin.hidden = !on;
      $$('button, input', line).forEach((el) => { if (el.closest('[data-dp-stepper]')) el.disabled = on; });
    }
    async send(line, qty) {
      if (!line) return;
      const key = line.dataset.key;
      const title = ($('[data-dp-line-title]', line) || {}).textContent || '';
      const sibling = line.nextElementSibling || line.previousElementSibling;
      const nextKey = sibling && sibling.dataset.key;
      const drawer = $('cart-drawer');
      if (this.ctrls.get(key)) this.ctrls.get(key).abort();
      const ctrl = new AbortController();
      this.ctrls.set(key, ctrl);
      if (drawer) drawer.clearAdded();
      const active = document.activeElement;
      const focusSel = !line.contains(active) ? null : active.matches('[data-dp-step]') ? `[data-dp-step="${active.dataset.dpStep}"]` : active.matches('[data-dp-qty]') ? '[data-dp-qty]' : null;
      this.busy(line, true);
      try {
        const data = await post('cart/change.js', { id: key, quantity: qty, sections: cartSections().join(','), sections_url: location.pathname }, ctrl.signal);
        applyCart(data.sections);
        const again = focusSel && qty > 0 && $(`[data-key="${CSS.escape(key)}"] ${focusSel}`, this.closest('cart-drawer') || document);
        if (again && !again.disabled) again.focus();
        emit('dp:cart:updated', { itemCount: data.item_count, source: qty === 0 ? 'remove' : 'change' });
        if (qty === 0) this.afterRemove(title, nextKey);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.busy(line, false);
        const input = $('[data-dp-qty]', line);
        input.value = input.defaultValue;
        input.dispatchEvent(new Event('change'));
        const box = $('[data-dp-line-error]', line);
        $('[data-dp-line-error-text]', box).textContent = errorText(err, err.status === 422 ? text('lineLimit') : text('generic'));
        box.hidden = false;
      } finally {
        if (this.ctrls.get(key) === ctrl) this.ctrls.delete(key);
      }
    }
    afterRemove(title, nextKey) {
      announce(text('removed').replace('__T__', title));
      const scope = this.closest('cart-drawer') || document;
      const target = (nextKey && $(`[data-key="${CSS.escape(nextKey)}"] [data-dp-line-title]`, scope))
        || $('[data-dp-empty-title]', scope) || $('#cart-drawer-title', scope) || $('[data-dp-cart-title]', scope);
      if (target) { target.setAttribute('tabindex', target.getAttribute('tabindex') || '-1'); target.focus(); }
    }
    // Cart page: replace the swap area from the returned section.
    render(html) {
      const next = $('[data-dp-cart-swap]', new DOMParser().parseFromString(html, 'text/html'));
      const cur = $('[data-dp-cart-swap]', this);
      if (!next || !cur) return;
      cur.replaceWith(document.importNode(next, true));
      this.watchBar();
    }
    // Mobile checkout bar: visible while the summary's Check out button is out of view.
    watchBar() {
      const bar = $('[data-dp-cbar]', this);
      const btn = $('[data-dp-checkout]', this);
      if (this.io) this.io.disconnect();
      document.documentElement.style.setProperty('--dp-bottom-bar', '0px');
      if (!bar || !btn) return;
      const small = matchMedia('(max-width: 1023.98px)');
      this.io = new IntersectionObserver(([entry]) => {
        const show = small.matches && !entry.isIntersecting;
        bar.toggleAttribute('data-visible', show);
        document.documentElement.style.setProperty('--dp-bottom-bar', show ? bar.offsetHeight + 'px' : '0px');
      });
      this.io.observe(btn);
    }
  }

  /* ---------- order note (drawer and cart page share it) ---------- */
  let noteTimer;
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-dp-note-toggle]');
    if (!t) return;
    const open = t.getAttribute('aria-expanded') !== 'true';
    t.setAttribute('aria-expanded', open);
    const body = $('#' + t.getAttribute('aria-controls'));
    body.hidden = !open;
    if (open) $('textarea', body).focus();
  });
  document.addEventListener('input', (e) => {
    const area = e.target.closest && e.target.closest('[data-dp-note-input]');
    if (!area) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(async () => {
      try {
        const cart = await post('cart/update.js', { note: area.value });
        $$('[data-dp-note-input]').forEach((o) => { if (o !== area) o.value = area.value; });
        emit('dp:cart:updated', { itemCount: cart.item_count, source: 'note' });
      } catch (err) { /* the note posts again with the form */ }
    }, 400);
  });

  /* ---------- quantity stepper ---------- */
  const syncStepper = (input) => {
    const minus = $('[data-dp-step="-1"]', input.closest('[data-dp-stepper]'));
    if (minus) minus.disabled = (parseInt(input.value, 10) || 1) <= (parseInt(input.min, 10) || 1);
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-dp-step]');
    if (!btn || btn.disabled) return;
    const input = $('[data-dp-qty]', btn.closest('[data-dp-stepper]'));
    const min = parseInt(input.min, 10) || 1;
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + +btn.dataset.dpStep);
    syncStepper(input);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
  document.addEventListener('change', (e) => {
    const input = e.target.matches && e.target.matches('[data-dp-qty]') && e.target;
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    if (!(parseInt(input.value, 10) >= min)) input.value = min;
    syncStepper(input);
  });

  /* ---------- video overlay (gallery and quick view) ---------- */
  const stopMedia = (slide) => {
    const video = slide && $('video', slide);
    if (video) video.pause();
    const frame = slide && $('iframe', slide);
    if (frame) { frame.remove(); const play = $('[data-dp-play]', slide); if (play) play.hidden = false; }
  };
  window.DPStopMedia = stopMedia;
  window.DPCommerce = { addItems, fail: (err) => DP.toast({ type: 'error', title: text('addTitle'), body: errorText(err) }) };
  document.addEventListener('click', (e) => {
    const play = e.target.closest('[data-dp-play]');
    if (!play) return;
    const slide = play.closest('[data-dp-slide]');
    const video = $('video', slide);
    play.hidden = true;
    if (video) { video.play(); video.focus(); return; }
    const frame = Object.assign(document.createElement('iframe'), { src: play.dataset.embedUrl, title: play.dataset.embedTitle || '', allowFullscreen: true });
    frame.allow = 'autoplay; encrypted-media; picture-in-picture';
    frame.className = 'dp-gallery__frame';
    slide.append(frame);
  });

  /* ---------- <variant-picker> ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.variants = JSON.parse($('[data-dp-variants]', this).textContent);
      this.select = $('[data-dp-variant-select]', this);
      this.groups = $$('fieldset[data-option-index]', this);
      this.addEventListener('change', (e) => { if (e.target.matches('input[type="radio"]')) this.update(); });
    }
    sold(i, value, sel) {
      return !this.variants.some((v) => v.available && v.options[i] === value && v.options.every((o, j) => j === i || o === sel[j]));
    }
    update() {
      const sel = this.groups.map((g) => ($('input:checked', g) || {}).value);
      this.groups.forEach((g, i) => {
        $$('input', g).forEach((input) => input.closest('label').toggleAttribute('data-sold-out', this.sold(i, input.value, sel)));
        $('[data-dp-legend-value]', g).textContent = sel[i];
        $('[data-dp-legend-soldout]', g).hidden = !this.sold(i, sel[i], sel);
      });
      const v = this.variants.find((x) => x.options.every((o, i) => o === sel[i]));
      const formId = this.dataset.formId;
      if (!v) return emit('dp:variant:change', { formId, variant: null });
      this.select.value = v.id;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      if (this.dataset.context === 'pdp') {
        const url = new URL(location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState(null, '', url);
      }
      emit('dp:variant:change', {
        formId,
        variant: {
          id: v.id, title: v.title, available: v.available, price: v.price, compareAtPrice: v.compare_at_price, sku: v.sku,
          featuredMediaId: v.featured_media_id, priceFormatted: v.price_formatted, compareAtPriceFormatted: v.compare_at_price_formatted
        }
      });
    }
  }

  /* ---------- <product-form>: add to cart for the product page and quick view ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.formId = this.dataset.formId;
      this.form = document.getElementById(this.formId);
      this.off = new AbortController();
      this.variant = undefined;
      if (this.form) this.form.addEventListener('submit', (e) => this.submit(e));
      this.addEventListener('input', (e) => { if (e.target.matches && e.target.matches('[data-dp-qty]')) this.alert(''); });
      document.addEventListener('dp:variant:change', (e) => { if (e.detail.formId === this.formId) this.onVariant(e.detail.variant); }, { signal: this.off.signal });
    }
    disconnectedCallback() { if (this.off) this.off.abort(); }
    atcs() { return $$(`[data-dp-atc][form="${this.formId}"]:not([data-dp-static])`); }
    alert(msg) {
      const box = $('[data-dp-alert]', this);
      if (!box) return;
      box.hidden = !msg;
      $('[data-dp-alert-text]', box).textContent = msg;
    }
    paint(state) {
      const v = this.variant;
      const labels = { loading: text('adding'), success: text('added'), idle: text('add') };
      let label = labels[state];
      let disabled = state === 'loading';
      if (state === 'idle' && v === null) { label = text('unavailable'); disabled = true; }
      else if (state === 'idle' && v && !v.available) { label = text('soldOut'); disabled = true; }
      this.atcs().forEach((b) => {
        b.dataset.state = state;
        b.disabled = disabled;
        b.setAttribute('aria-busy', state === 'loading');
        $('[data-dp-label]', b).textContent = label;
      });
    }
    async submit(e) {
      e.preventDefault();
      if (this.busy || this.variant === null || (this.variant && !this.variant.available)) return;
      const data = new FormData(this.form);
      const qv = this.closest('quick-view');
      this.busy = true;
      this.alert('');
      this.paint('loading');
      try {
        await addItems([{ id: +data.get('id'), quantity: parseInt(data.get('quantity'), 10) || 1 }], 'add', qv && qv.trigger, () => qv && qv.close({ restore: false }));
        this.paint('success');
        const reset = () => { clearTimeout(timer); document.removeEventListener('dp:overlay:change', onClose); if (this.isConnected) this.paint('idle'); };
        const onClose = (ev) => { if (!ev.detail.open) reset(); };
        const timer = setTimeout(reset, 2000);
        document.addEventListener('dp:overlay:change', onClose);
      } catch (err) {
        this.paint('idle');
        this.alert(errorText(err));
      } finally { this.busy = false; }
    }
    onVariant(v) {
      this.variant = v;
      this.alert('');
      if (v) $$('[data-dp-price-for]', this).forEach((el) => { el.hidden = el.dataset.dpPriceFor !== String(v.id); });
      $$('[data-dp-when]', this).forEach((el) => { el.hidden = el.dataset.dpWhen === 'available' ? !!v && !v.available : !v || v.available; });
      const sku = $('[data-dp-sku]', this);
      if (sku && v) { sku.textContent = sku.dataset.tpl.replace('__SKU__', v.sku); sku.hidden = !v.sku; }
      if (v) {
        const line = $('[data-dp-notify-line]', this);
        if (line) line.textContent = line.dataset.tpl.replace('__V__', v.title);
        const body = $('[data-dp-notify-body]', this);
        if (body) body.value = body.dataset.tpl.replace('__V__', v.title).replace('__ID__', v.id);
        const base = (el) => el.dataset.base + '?variant=' + v.id;
        $$('[data-dp-details-link]', this).forEach((a) => { a.href = base(a); });
        $$('[data-dp-notify-link]', this).forEach((a) => { a.href = base(a) + '#notify'; });
        const msg = $('[data-dp-announce]', this);
        if (msg) msg.textContent = msg.dataset.tpl.replace('__V__', v.title).replace('__P__', v.priceFormatted).replace('__A__', v.available ? text('inStockWord') : text('soldOutWord'));
      }
      this.paint('idle');
    }
  }

  /* ---------- <qv-gallery>: quick view media ---------- */
  class QvGallery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.slides = $$('[data-dp-slide]', this);
      this.thumbs = $$('[data-dp-thumb]', this);
      this.at = +this.dataset.current || 0;
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-dp-thumb]');
        const nav = e.target.closest('[data-dp-nav]');
        if (thumb) this.select(+thumb.dataset.index);
        if (nav) this.select((this.at + +nav.dataset.dpNav + this.slides.length) % this.slides.length);
      });
      this.off = new AbortController();
      document.addEventListener('dp:variant:change', (e) => {
        const v = e.detail.formId === this.dataset.formId && e.detail.variant;
        const i = v && v.featuredMediaId ? this.slides.findIndex((s) => s.dataset.mediaId === String(v.featuredMediaId)) : -1;
        if (i > -1) this.select(i);
      }, { signal: this.off.signal });
    }
    disconnectedCallback() { if (this.off) this.off.abort(); }
    select(i) {
      stopMedia(this.slides[this.at]);
      this.at = i;
      this.slides.forEach((s, n) => s.toggleAttribute('data-current', n === i));
      this.thumbs.forEach((t, n) => t.setAttribute('aria-pressed', n === i));
      const count = $('[data-dp-counter]', this);
      if (count) count.textContent = `${i + 1} / ${this.slides.length}`;
      if (this.thumbs[i]) this.thumbs[i].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /* ---------- <quick-view> ---------- */
  class QuickView extends Overlay {
    connectedCallback() {
      super.connectedCallback();
      if (this._wired) return;
      this._wired = true;
      this.cache = new Map();
      this.body = $('[data-dp-qv-body]', this);
      this.skeleton = $('template[data-dp-qv-skeleton]', this);
      this.loadingLabel = this.panel.getAttribute('aria-label');
    }
    async show(url, trigger) {
      if (this.ctrl) this.ctrl.abort();
      const ctrl = (this.ctrl = new AbortController());
      this.trigger = trigger;
      this.body.replaceChildren(this.skeleton.content.cloneNode(true));
      this.panel.setAttribute('aria-label', this.loadingLabel);
      this.panel.removeAttribute('aria-labelledby');
      this.open({ returnTo: trigger });
      try {
        let html = this.cache.get(url);
        if (!html) {
          const res = await fetch(url + (url.includes('?') ? '&' : '?') + 'section_id=quick-view', { signal: ctrl.signal });
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const section = res.ok && doc.getElementById('shopify-section-quick-view');
          if (!section || !$('product-form', section)) throw new Error('no quick view');
          html = section.innerHTML;
          this.cache.set(url, html);
        }
        if (ctrl !== this.ctrl) return;
        this.body.innerHTML = html;
        const form = $('product-form', this.body);
        this.panel.removeAttribute('aria-label');
        this.panel.setAttribute('aria-labelledby', form.dataset.titleId);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.close({ restore: false });
        location.href = url;
      }
    }
  }

  const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);
  define('product-form', ProductForm);
  define('variant-picker', VariantPicker);
  define('qv-gallery', QvGallery);
  define('cart-items', CartItems);
  define('cart-drawer', CartDrawer);
  define('quick-view', QuickView);

  /* ---------- delegated listeners ---------- */
  const plain = (e) => e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
  document.addEventListener('click', (e) => {
    const qv = e.target.closest('[data-dp-quick-view]');
    if (qv && plain(e) && $('quick-view')) {
      e.preventDefault();
      $('quick-view').show(qv.dataset.dpQuickView, qv);
      return;
    }
    const open = e.target.closest('[data-dp-cart-open]');
    if (open && plain(e) && $('cart-drawer')) {
      e.preventDefault();
      $('cart-drawer').open({ returnTo: open });
    }
  });

  const setCard = (btn, state) => {
    btn.dataset.state = state;
    btn.disabled = state === 'loading';
    btn.setAttribute('aria-busy', state === 'loading');
    const label = { loading: btn.dataset.labelAdding, success: btn.dataset.labelAdded, idle: btn.dataset.labelIdle }[state];
    const el = $('[data-dp-label]', btn);
    if (el && label) el.textContent = label;
  };
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest && e.target.closest('form[data-dp-quick-add]');
    if (!form) return;
    e.preventDefault();
    const btn = $('button[type="submit"]', form);
    if (btn.dataset.state === 'success') { if ($('cart-drawer')) $('cart-drawer').open({ returnTo: btn }); return; }
    if (form._busy) return;
    form._busy = true;
    setCard(btn, 'loading');
    try {
      await addItems([{ id: +form.elements.id.value, quantity: +(form.elements.quantity || {}).value || 1 }], 'add', btn);
      setCard(btn, 'success');
      setTimeout(() => setCard(btn, 'idle'), 1800);
    } catch (err) {
      setCard(btn, 'idle');
      DP.toast({ type: 'error', title: text('addTitle'), body: errorText(err) });
    } finally { form._busy = false; }
  });
})();
